import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, TimeScale } from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Shield, 
  TrendingUp, 
  ShieldCheck, 
  Globe, 
  AlertTriangle, 
  FileText,
  Target,
  Network,
  Ban,
  Clock,
  Eye,
  Download,
  LogOut,
  Lock,
  Radar,
  MapPin,
  Search,
  Bell,
  Users,
  Server,
  BarChart3,
  PieChart,
  ChevronDown,
  Activity,
  Zap,
  Monitor,
  Smartphone,
  Tablet,
  ShieldAlert,
  Database,
  CheckCircle
} from 'lucide-react';
import ItLoganLogo from './img/itlogan.svg';

// Fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, TimeScale);

// Mock data generators for enhanced security dashboard
const generateMockSecurityData = () => {
  const attackTypes = ['Phishing', 'Malware', 'DDoS', 'SQL Injection', 'Brute Force', 'XSS'];
  const countries = ['Russia', 'China', 'North Korea', 'Iran', 'Brazil', 'India', 'Unknown'];
  const data = [];
  
  for (let i = 0; i < 50; i++) {
    data.push({
      id: i + 1,
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      eventType: attackTypes[Math.floor(Math.random() * attackTypes.length)],
      sourceIP: `${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}`,
      destination: 'honeypot.itlogan.com',
      threatLevel: ['Low', 'Medium', 'High', 'Critical'][Math.floor(Math.random() * 4)],
      country: countries[Math.floor(Math.random() * countries.length)],
      blocked: Math.random() > 0.3,
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    });
  }
  return data;
};

const generateGeoData = () => {
  return [
    { lat: 55.7558, lng: 37.6176, country: 'Russia', attacks: 145, severity: 'high' },
    { lat: 39.9042, lng: 116.4074, country: 'China', attacks: 123, severity: 'high' },
    { lat: 39.0392, lng: 125.7625, country: 'North Korea', attacks: 89, severity: 'critical' },
    { lat: 35.6892, lng: 51.3890, country: 'Iran', attacks: 67, severity: 'medium' },
    { lat: -14.2350, lng: -51.9253, country: 'Brazil', attacks: 45, severity: 'medium' },
    { lat: 20.5937, lng: 78.9629, country: 'India', attacks: 34, severity: 'low' },
    { lat: 52.5200, lng: 13.4050, country: 'Germany', attacks: 23, severity: 'low' },
    { lat: 48.8566, lng: 2.3522, country: 'France', attacks: 19, severity: 'low' }
  ];
};

function Dashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminSecret, setAdminSecret] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [realTimeAlerts, setRealTimeAlerts] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState([]);
  const [threatLevel, setThreatLevel] = useState('LOW');
  const [blacklistedIPs, setBlacklistedIPs] = useState(new Set());
  
  // Enhanced security dashboard state
  const [mockSecurityData] = useState(generateMockSecurityData());
  const [geoData] = useState(generateGeoData());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [screenSize, setScreenSize] = useState('desktop');
  
  // Live Clock State
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const wsRef = useRef(null);
  const updateIntervalRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const dropdownRef = useRef(null);

  // Logout function
  const handleLogout = () => {
    setIsAuthenticated(false);
    setAnalytics(null);
    setAdminSecret('');
    setError('');
    setRealTimeAlerts([]);
    setNotifications([]);
    
    // Clear any intervals
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    
    // Navigate back to login page
    navigate('/');
  };

  // Authentication
  const handleLogin = async () => {
    if (adminSecret === '#PuT@ng!n@m0n5HyP3k4') {
      setIsAuthenticated(true);
      await fetchAnalytics();
      setupRealTimeUpdates();
    } else {
      setError('Invalid admin secret');
    }
  };

  // Real-time updates via polling
  const setupRealTimeUpdates = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
    }
    
    updateIntervalRef.current = setInterval(() => {
      if (isAuthenticated) {
        fetchAnalytics();
      }
    }, 5000); // Update every 5 seconds
  };

  const fetchAnalytics = async () => {
    try {
      const apiUrl = process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:5000/api/analytics';
      const response = await fetch(`${apiUrl}?secret=${adminSecret || '#PuT@ng!n@m0n5HyP3k4'}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        setError('');
        
        // Process threat level
        const recentAttempts = data.logs?.filter(log => {
          const logTime = new Date(log[0]);
          const oneHourAgo = new Date(Date.now() - 3600000);
          return logTime > oneHourAgo;
        }).length || 0;

        if (recentAttempts > 10) setThreatLevel('HIGH');
        else if (recentAttempts > 5) setThreatLevel('MEDIUM');
        else setThreatLevel('LOW');
        
        // Check for new alerts
        if (data.logs && data.logs.length > 0) {
          const latestAttempt = data.logs[0];
          const lastAlert = realTimeAlerts[0];
          if (!lastAlert || latestAttempt[0] !== lastAlert.timestamp) {
            const newAlert = {
              id: Date.now(),
              timestamp: latestAttempt[0],
              ip: latestAttempt[1],
              username: latestAttempt[5],
              type: 'Login Attempt',
              severity: blacklistedIPs.has(latestAttempt[1]) ? 'HIGH' : 'MEDIUM'
            };
            
            setRealTimeAlerts(prev => [newAlert, ...prev.slice(0, 4)]);
            addNotification(newAlert);
          }
        }
      } else {
        setError('Unauthorized or failed to fetch analytics');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (alert) => {
    const notification = {
      id: Date.now(),
      message: `New ${alert.type} from ${alert.ip}`,
      severity: alert.severity,
      timestamp: new Date().toLocaleTimeString()
    };
    
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);
    
    // Auto-remove notification after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 10000);
  };

  const addToBlacklist = (ip) => {
    setBlacklistedIPs(prev => new Set([...prev, ip]));
    addNotification({
      type: 'IP Blocked',
      ip: ip,
      severity: 'HIGH'
    });
  };

  // Enhanced export functions with better error handling
  const exportData = (format) => {
    try {
      if (!analytics && !mockSecurityData.length) {
        alert('No data available to export');
        return;
      }
      
      if (format === 'csv') {
        exportCSV();
      } else if (format === 'pdf') {
        generatePDF();
      }
    } catch (error) {
      console.error('Export Error:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  // Responsive breakpoint detection with better handling
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) setScreenSize('mobile');
      else if (width < 1024) setScreenSize('tablet');
      else setScreenSize('desktop');
    };
    
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    
    // Close dropdown on resize
    const handleResize = () => {
      setDropdownOpen(false);
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', updateScreenSize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.relative')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Initialize enhanced world map with better error handling
  useEffect(() => {
    if (mapRef.current && !mapInstance.current && activeTab === 'geography') {
      try {
        mapInstance.current = L.map(mapRef.current, {
          center: [20, 0],
          zoom: 2,
          zoomControl: true,
          scrollWheelZoom: false,
          worldCopyJump: true
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 18,
        }).addTo(mapInstance.current);

        // Add attack markers with error handling
        geoData.forEach((point, index) => {
          try {
            const color = point.severity === 'critical' ? '#dc2626' : 
                         point.severity === 'high' ? '#ea580c' :
                         point.severity === 'medium' ? '#ca8a04' : '#16a34a';
            
            const marker = L.circleMarker([point.lat, point.lng], {
              radius: Math.min(Math.max(point.attacks / 10, 5), 20),
              fillColor: color,
              color: color,
              weight: 2,
              opacity: 0.8,
              fillOpacity: 0.6
            }).addTo(mapInstance.current);

            marker.bindPopup(`
              <div class="map-popup">
                <h3 style="color: #3b82f6; margin-bottom: 8px;">${point.country}</h3>
                <p style="margin: 4px 0;">Attacks: <strong>${point.attacks}</strong></p>
                <p style="margin: 4px 0;">Severity: <strong style="color: ${color};">${point.severity}</strong></p>
              </div>
            `);
          } catch (error) {
            console.warn(`Error adding marker for ${point.country}:`, error);
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }

    return () => {
      if (mapInstance.current) {
        try {
          mapInstance.current.remove();
          mapInstance.current = null;
        } catch (error) {
          console.warn('Error cleaning up map:', error);
        }
      }
    };
  }, [geoData, activeTab]);

  // Enhanced export functions
  const exportCSV = () => {
    try {
      const allData = analytics?.logs || mockSecurityData;
      const headers = ['Timestamp', 'Event Type', 'Source IP', 'Destination', 'Threat Level', 'Country', 'Blocked', 'User Agent'];
      
      const csvContent = [
        headers.join(','),
        ...allData.slice(0, 1000).map(row => {
          try {
            if (Array.isArray(row)) {
              // Handle existing analytics format
              return [
                `"${row[0] || new Date().toISOString()}"`,
                '"Login Attempt"',
                `"${row[1] || 'Unknown'}"`,
                '"honeypot.itlogan.com"',
                '"Medium"',
                '"Unknown"',
                '"Yes"',
                `"${row[2] || 'Unknown User Agent'}"`
              ].join(',');
            } else {
              // Handle mock security data format
              return [
                `"${row.timestamp || new Date().toISOString()}"`,
                `"${row.eventType || 'Unknown'}"`,
                `"${row.sourceIP || 'Unknown'}"`,
                `"${row.destination || 'honeypot.itlogan.com'}"`,
                `"${row.threatLevel || 'Medium'}"`,
                `"${row.country || 'Unknown'}"`,
                `"${row.blocked ? 'Yes' : 'No'}"`,
                `"${row.userAgent || 'Unknown User Agent'}"`
              ].join(',');
            }
          } catch (error) {
            console.error('Error processing row:', error);
            return '"Error","Error","Error","Error","Error","Error","Error","Error"';
          }
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `honeypot_security_log_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      // Show success notification
      addNotification({
        type: 'CSV Exported',
        ip: 'System',
        severity: 'LOW'
      });
      
    } catch (error) {
      console.error('CSV Export Error:', error);
      alert('Error exporting CSV data. Please try again.');
      
      // Show error notification
      addNotification({
        type: 'CSV Export Failed',
        ip: 'System',
        severity: 'HIGH'
      });
    }
  };

  const generatePDF = async () => {
    try {
      // Show loading state
      const button = document.querySelector('[data-pdf-button]');
      if (button) {
        button.disabled = true;
        button.textContent = 'Generating PDF...';
      }

      const doc = new jsPDF();
      
      // Header with logo
      doc.setFontSize(20);
      doc.setTextColor(14, 56, 177);
      doc.text('Honeypot Security Center Report', 20, 30);
      
      // Reset color for body text
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 45);
      doc.text(`Time Range: Last ${selectedTimeRange}`, 20, 55);
      doc.text(`Report ID: HSC-${Date.now()}`, 20, 65);
      
      // Summary stats with better formatting
      doc.setFontSize(16);
      doc.setTextColor(14, 56, 177);
      doc.text('Executive Security Summary', 20, 85);
      
      doc.setTextColor(0, 0, 0);
      const totalEvents = analytics?.total || mockSecurityData.length;
      const blockedEvents = mockSecurityData.filter(d => d.blocked).length;
      const criticalEvents = mockSecurityData.filter(d => d.threatLevel === 'Critical').length;
      const highEvents = mockSecurityData.filter(d => d.threatLevel === 'High').length;
      const uniqueIPs = new Set(mockSecurityData.map(d => d.sourceIP)).size;
      
      doc.setFontSize(12);
      doc.text(`• Total Security Events: ${totalEvents}`, 25, 100);
      doc.text(`• Blocked Attacks: ${blockedEvents} (${Math.round((blockedEvents/totalEvents)*100)}%)`, 25, 110);
      doc.text(`• Critical Threats: ${criticalEvents}`, 25, 120);
      doc.text(`• High Priority Threats: ${highEvents}`, 25, 130);
      doc.text(`• Unique Attack Sources: ${uniqueIPs}`, 25, 140);
      doc.text(`• Current Threat Level: ${threatLevel}`, 25, 150);
      
      // Attack types breakdown
      doc.setFontSize(14);
      doc.setTextColor(14, 56, 177);
      doc.text('Attack Vector Analysis', 20, 170);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      const attackTypes = {};
      mockSecurityData.forEach(event => {
        attackTypes[event.eventType] = (attackTypes[event.eventType] || 0) + 1;
      });
      
      let yPos = 185;
      Object.entries(attackTypes).forEach(([type, count]) => {
        doc.text(`• ${type}: ${count} events`, 25, yPos);
        yPos += 10;
      });
      
      // Table of recent events with better error handling
      const tableData = mockSecurityData.slice(0, 15).map(row => {
        try {
          return [
            new Date(row.timestamp).toLocaleString() || 'N/A',
            row.eventType || 'Unknown',
            row.sourceIP || 'Unknown',
            row.threatLevel || 'Medium',
            row.blocked ? 'Blocked' : 'Allowed',
            row.country || 'Unknown'
          ];
        } catch (e) {
          return ['Error', 'Error', 'Error', 'Error', 'Error', 'Error'];
        }
      });
      
      // Add new page if needed
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(14);
      doc.setTextColor(14, 56, 177);
      doc.text('Recent Security Events (Last 15)', 20, yPos + 10);
      
      // Use autoTable for better table generation
      autoTable(doc, {
        head: [['Timestamp', 'Event Type', 'Source IP', 'Threat Level', 'Status', 'Country']],
        body: tableData,
        startY: yPos + 20,
        styles: { 
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak',
          columnWidth: 'wrap'
        },
        headStyles: {
          fillColor: [14, 56, 177],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: {columnWidth: 35}, // Timestamp
          1: {columnWidth: 25}, // Event Type
          2: {columnWidth: 30}, // Source IP
          3: {columnWidth: 20}, // Threat Level
          4: {columnWidth: 20}, // Status
          5: {columnWidth: 25}  // Country
        }
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Generated by IT Logan Honeypot Security Center | Page ${i} of ${pageCount}`, 20, doc.internal.pageSize.height - 10);
        doc.text(`© ${new Date().getFullYear()} Ramon Logan Jr. - All Rights Reserved`, 20, doc.internal.pageSize.height - 5);
      }
      
      const fileName = `honeypot_security_report_${new Date().toISOString().split('T')[0]}_${Date.now()}.pdf`;
      
      // Save the PDF
      await new Promise((resolve) => {
        setTimeout(() => {
          doc.save(fileName);
          resolve();
        }, 100);
      });
      
      // Show success notification
      addNotification({
        type: 'PDF Generated Successfully',
        ip: 'System',
        severity: 'LOW'
      });
      
      // Reset button state
      if (button) {
        button.disabled = false;
        button.innerHTML = `<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>PDF`;
      }
      
    } catch (error) {
      console.error('PDF Generation Error:', error);
      
      // More user-friendly error message
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Error generating PDF report: ${errorMessage}\n\nPlease try again. If the problem persists, check your browser's download settings.`);
      
      // Show error notification
      addNotification({
        type: 'PDF Generation Failed',
        ip: 'System',
        severity: 'HIGH'
      });
      
      // Reset button state
      const button = document.querySelector('[data-pdf-button]');
      if (button) {
        button.disabled = false;
        button.innerHTML = `<svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>PDF`;
      }
    }
  };

  // Simulated intrusion detection
  const simulateIntrusionDetection = () => {
    const mockAttackerData = {
      ip: '192.168.1.100',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      attemptedPage: '/admin',
      location: 'Unknown Location'
    };

    // Visual notification
    alert(`SECURITY ALERT\n\nUnauthorized access attempt detected!\n\nIP: ${mockAttackerData.ip}\nTime: ${mockAttackerData.timestamp}\nAttempted Access: ${mockAttackerData.attemptedPage}\n\nNOTE: In a real implementation, complete logs would be automatically sent to:\n• rloganjr1025@gmail.com\n• absons.ramon@gmail.com\n\nThis is a demonstration - actual email sending requires backend infrastructure.`);
    
    console.log('SECURITY LOG:', mockAttackerData);
    
    // Add to notifications
    addNotification({
      type: 'Simulated Attack',
      ip: mockAttackerData.ip,
      severity: 'HIGH'
    });
  };

  const getResponsiveColumns = () => {
    switch (screenSize) {
      case 'mobile': return 'grid-cols-1';
      case 'tablet': return 'grid-cols-2';
      default: return 'grid-cols-3';
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      setupRealTimeUpdates();
    }
    
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [isAuthenticated]);

  // Live Clock useEffect
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen glassmorphism-bg flex items-center justify-center relative overflow-hidden">
        {/* Background animated elements */}
        <div className="glassmorphism-orb glassmorphism-orb-1"></div>
        <div className="glassmorphism-orb glassmorphism-orb-2"></div>
        <div className="glassmorphism-orb glassmorphism-orb-3"></div>
        {/* Background Glass Orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full opacity-20 animate-float" style={{ background: 'linear-gradient(135deg, #0e38b1, #1e40af)', filter: 'blur(40px)' }}></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-20 animate-float" style={{ background: 'linear-gradient(135deg, #0e38b1, #3b82f6)', filter: 'blur(40px)', animationDelay: '2s' }}></div>
        </div>
        
        <div className="relative z-10 w-full max-w-md mx-4">
          <div className="glassmorphism-card rounded-3xl relative overflow-hidden">
            {/* Glass highlight */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-20"></div>
            
            <div className="text-center mb-8">
              <div className="mb-6">
                <img 
                  src={ItLoganLogo} 
                  alt="IT Logan" 
                  className="mx-auto h-16 w-auto opacity-90"
                />
              </div>
              <div className="flex items-center justify-center mb-4">
                <Shield 
                  className="text-3xl mr-3 w-8 h-8"
                  style={{ color: '#0e38b1' }}
                />
                <h2 className="text-3xl font-bold text-white">Security Dashboard</h2>
              </div>
              <p className="text-gray-300">Enter admin credentials to access the monitoring system</p>
            </div>
            
            <div className="space-y-6">
              <div className="relative">
                <Lock 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                />
                <input
                  type="password"
                  placeholder="Enter admin secret"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  className="glassmorphism-input w-full pl-12 pr-4 py-4 rounded-xl text-white transition-all duration-300 focus:outline-none focus:ring-2 placeholder-gray-400"
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
              </div>
              
              <button
                onClick={handleLogin}
                className="glassmorphism-button w-full py-4 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 active:scale-95 relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center justify-center">
                  <Shield className="mr-2 w-5 h-5" />
                  Access Dashboard
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
              </button>
              
              {error && (
                <div className="text-center">
                  <p className="text-red-300 animate-pulse flex items-center justify-center">
                    <AlertTriangle className="mr-2 w-5 h-5" />
                    {error}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
              <div className="text-sm text-gray-300 font-medium">
                Ramon Logan Jr. © 2025 IT Logan. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <div className="min-h-screen glassmorphism-bg flex items-center justify-center">
        {/* Background animated elements */}
        <div className="glassmorphism-orb glassmorphism-orb-1"></div>
        <div className="glassmorphism-orb glassmorphism-orb-2"></div>
        <div className="glassmorphism-orb glassmorphism-orb-3"></div>
        <div className="text-white text-center">
          <div className="relative">
            <div className="animate-spin w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <div className="absolute inset-0 animate-ping w-16 h-16 border-4 border-blue-300 border-t-transparent rounded-full mx-auto opacity-20"></div>
          </div>
          <p className="text-xl">Initializing Security Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative glassmorphism-bg">
      {/* Floating Time Range Selector - Above Header */}
      <div className="fixed top-4 right-4 z-50 flex items-center space-x-3" ref={dropdownRef}>
        {/* Mobile Clock Display */}
        <div className="lg:hidden px-3 py-2 rounded-lg text-xs text-gray-300"
             style={{
               background: 'linear-gradient(135deg, rgba(13, 13, 13, 0.9), rgba(17, 24, 39, 0.9))',
               backdropFilter: 'blur(20px)',
               border: '1px solid rgba(255, 255, 255, 0.1)',
               boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
             }}>
          {currentTime.toLocaleDateString('en-US', { 
            month: 'short',
            day: 'numeric'
          })} • {currentTime.toLocaleTimeString('en-US', { 
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 flex items-center space-x-2 shadow-2xl hover:scale-105 active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 13, 13, 0.9), rgba(17, 24, 39, 0.9))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            }}
          >
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="text-white">Last {selectedTimeRange}</span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {dropdownOpen && (
            <>
              {/* Enhanced backdrop with higher z-index */}
              <div 
                className="fixed inset-0" 
                style={{ zIndex: 9999998 }}
                onClick={() => setDropdownOpen(false)}
              ></div>
              
              <div className="absolute right-0 mt-2 w-48 rounded-xl shadow-2xl border border-white/20 overflow-hidden"
                   style={{
                     background: 'linear-gradient(135deg, rgba(13, 13, 13, 0.95), rgba(17, 24, 39, 0.95))',
                     backdropFilter: 'blur(20px)',
                     boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                     zIndex: 9999999
                   }}>
                {['1h', '6h', '24h', '7d', '30d'].map((range, index) => (
                  <button
                    key={range}
                    onClick={() => {
                      setSelectedTimeRange(range);
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-3 transition-all duration-200 text-sm font-medium hover:bg-white/10 ${
                      selectedTimeRange === range 
                        ? 'bg-gradient-to-r from-blue-600/30 to-blue-500/20 text-blue-300 border-r-2 border-blue-400' 
                        : 'text-white hover:text-blue-200'
                    } ${index === 0 ? 'rounded-t-xl' : ''} ${index === 4 ? 'rounded-b-xl' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Last {range}</span>
                      {selectedTimeRange === range && (
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Background Glass Elements */}
      <div className="glassmorphism-orb glassmorphism-orb-1"></div>
      <div className="glassmorphism-orb glassmorphism-orb-2"></div>
      <div className="glassmorphism-orb glassmorphism-orb-3"></div>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10 animate-float" style={{ background: 'linear-gradient(135deg, #0e38b1, #3b82f6)', filter: 'blur(60px)' }}></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-10 animate-float" style={{ background: 'linear-gradient(135deg, #1e40af, #0e38b1)', filter: 'blur(60px)', animationDelay: '3s' }}></div>
      </div>

      {/* Notifications */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse space-y-reverse space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 rounded-lg shadow-lg animate-slide-in border-l-4 backdrop-blur-lg ${
              notification.severity === 'HIGH' ? 'border-red-500' :
              notification.severity === 'MEDIUM' ? 'border-yellow-500' :
              'border-blue-500'
            }`}
            style={{
              background: notification.severity === 'HIGH' ? 'rgba(239, 68, 68, 0.1)' :
                         notification.severity === 'MEDIUM' ? 'rgba(251, 146, 60, 0.1)' :
                         'rgba(14, 56, 177, 0.1)'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bell 
                  className={`mr-2 w-4 h-4 ${
                    notification.severity === 'HIGH' ? 'text-red-400' :
                    notification.severity === 'MEDIUM' ? 'text-yellow-400' :
                    'text-blue-400'
                  }`}
                />
                <span className="text-sm text-white">{notification.message}</span>
              </div>
              <span className="text-xs opacity-70 text-gray-300">{notification.timestamp}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <header 
        className="glassmorphism-header backdrop-blur-xl border-b px-6 py-4 sticky top-0 z-50"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <img 
                src={ItLoganLogo} 
                alt="IT Logan" 
                className="h-8 w-auto"
              />
              <h1 className="text-2xl font-bold text-white">
                Honeypot Security Center
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full animate-pulse ${threatLevel === 'HIGH' ? 'bg-red-500' : threatLevel === 'MEDIUM' ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
              <span className="text-sm text-gray-300">
                Threat Level: <span className={`font-semibold ${threatLevel === 'HIGH' ? 'text-red-400' : threatLevel === 'MEDIUM' ? 'text-yellow-400' : 'text-green-400'}`}>{threatLevel}</span>
              </span>
            </div>
          </div>
          
          {/* Live Clock Display */}
          <div className="flex items-center space-x-4">
            <div className="hidden lg:flex items-center space-x-2 px-3 py-2 rounded-lg"
                 style={{
                   background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
                   backdropFilter: 'blur(20px)',
                   border: '1px solid rgba(255, 255, 255, 0.15)'
                 }}>
              <Clock className="w-4 h-4 text-gray-400" />
              <div className="text-sm text-gray-300">
                {currentTime.toLocaleDateString('en-US', { 
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })} • {currentTime.toLocaleTimeString('en-US', { 
                  hour12: false,
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center space-x-2 sm:space-x-4 gap-2">
            {/* Export Buttons with improved styling */}
            <button
              onClick={exportCSV}
              className="px-3 sm:px-4 py-2 rounded-lg text-sm transition-all duration-300 hover:scale-105 active:scale-95 flex items-center space-x-1 sm:space-x-2 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #059669, #10b981)',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
              }}
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export CSV</span>
              <span className="md:hidden">CSV</span>
            </button>
            
            <button
              onClick={generatePDF}
              data-pdf-button
              className="px-3 sm:px-4 py-2 rounded-lg text-sm transition-all duration-300 hover:scale-105 active:scale-95 flex items-center space-x-1 sm:space-x-2 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
              }}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden md:inline">Generate PDF</span>
              <span className="md:hidden">PDF</span>
            </button>

            {/* Simulate Intrusion Button with better styling */}
            <button
              onClick={simulateIntrusionDetection}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-all duration-300 text-sm shadow-lg hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
              }}
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden lg:inline">Simulate Attack</span>
              <span className="lg:hidden">Alert</span>
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition-all duration-300 text-sm shadow-lg hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 4px 12px rgba(75, 85, 99, 0.3)'
              }}
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden lg:inline">Logout</span>
            </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav 
        className="backdrop-blur px-3 sm:px-6 overflow-x-auto relative z-10"
        style={{
          background: 'rgba(14, 56, 177, 0.05)'
        }}
      >
        <div className="flex space-x-4 sm:space-x-8 min-w-max">
          {[
            { id: 'overview', name: 'Overview', icon: TrendingUp, shortName: 'Overview' },
            { id: 'attacks', name: 'Attack Analysis', icon: ShieldCheck, shortName: 'Attacks' },
            { id: 'geography', name: 'Geographic Intel', icon: Globe, shortName: 'Geography' },
            { id: 'threats', name: 'Threat Intelligence', icon: AlertTriangle, shortName: 'Threats' },
            { id: 'reports', name: 'Reports & Export', icon: FileText, shortName: 'Reports' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 sm:py-4 px-2 font-medium text-sm transition-all flex items-center space-x-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={{
                boxShadow: activeTab === tab.id ? '0 0 20px rgba(0, 66, 255, 0.6), 0 0 40px rgba(0, 66, 255, 0.3)' : 'none',
                textShadow: activeTab === tab.id ? '0 0 10px rgba(0, 66, 255, 0.8)' : 'none',
                color: activeTab === tab.id ? '#0042ff' : undefined
              }}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.name}</span>
              <span className="sm:hidden">{tab.shortName}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Real-time Alerts Bar */}
      {realTimeAlerts.length > 0 && (
        <div 
          className="border-b px-6 py-3 backdrop-blur"
          style={{
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(251, 146, 60, 0.1))',
            borderColor: 'rgba(239, 68, 68, 0.3)'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <AlertTriangle className="text-red-300 w-4 h-4" />
              <span className="text-red-300 font-semibold">Live Alert</span>
              <span className="text-red-200">
                {realTimeAlerts[0].type} from {realTimeAlerts[0].ip} - {new Date(realTimeAlerts[0].timestamp).toLocaleTimeString()}
              </span>
            </div>
            <button
              onClick={() => addToBlacklist(realTimeAlerts[0].ip)}
              className="px-3 py-1 rounded text-sm transition-all hover:scale-105 flex items-center space-x-1"
              style={{
                background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
              }}
            >
              <Ban className="w-4 h-4" />
              <span>Block IP</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-6 relative z-10">
        {activeTab === 'overview' && <OverviewTab analytics={analytics} threatLevel={threatLevel} blacklistedIPs={blacklistedIPs} />}
        {activeTab === 'attacks' && <AttacksTab analytics={analytics} mockData={mockSecurityData} />}
        {activeTab === 'geography' && <GeographyTab analytics={analytics} geoData={geoData} mapRef={mapRef} />}
        {activeTab === 'threats' && <ThreatsTab analytics={analytics} blacklistedIPs={blacklistedIPs} addToBlacklist={addToBlacklist} />}
        {activeTab === 'reports' && <ReportsTab analytics={analytics} exportData={exportData} exportCSV={exportCSV} generatePDF={generatePDF} />}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 px-6 relative z-10">
        <div className="text-sm text-gray-300 font-medium">
          Ramon Logan Jr. © 2025 IT Logan. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ analytics, threatLevel, blacklistedIPs }) {
  const stats = analytics ? {
    totalAttempts: analytics.total || 0,
    uniqueIPs: analytics.top_ips?.length || 0,
    topCountry: 'Unknown',
    avgPerHour: Math.round((analytics.total || 0) / 24),
    blockedIPs: blacklistedIPs.size
  } : {};

  // Generate time series data
  const generateTimeSeriesData = () => {
    const hours = [];
    const attempts = [];
    for (let i = 23; i >= 0; i--) {
      const hour = new Date(Date.now() - i * 3600000);
      hours.push(hour.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      attempts.push(Math.floor(Math.random() * 10) + (analytics?.total || 0) / 24);
    }
    return { hours, attempts };
  };

  const timeData = generateTimeSeriesData();

  const chartData = {
    labels: timeData.hours,
    datasets: [
      {
        label: 'Attack Attempts',
        data: timeData.attempts,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const attackTypeData = {
    labels: ['Brute Force', 'SQL Injection', 'XSS', 'CSRF', 'Malware'],
    datasets: [
      {
        data: [45, 25, 15, 10, 5],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(234, 179, 8, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(99, 102, 241, 0.8)'
        ],
        borderWidth: 0,
      },
    ],
  };

  const severityData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [5, 15, 35, 45],
        backgroundColor: [
          'rgba(220, 38, 38, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(253, 224, 71, 0.8)',
          'rgba(34, 197, 94, 0.8)'
        ],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard title="Total Attempts" value={stats.totalAttempts} icon={Target} color="blue" trend="+12%" />
        <StatCard title="Unique IPs" value={stats.uniqueIPs} icon={Network} color="green" trend="+5%" />
        <StatCard title="Blocked IPs" value={stats.blockedIPs} icon={Ban} color="red" trend="+2" />
        <StatCard title="Avg/Hour" value={stats.avgPerHour} icon={Clock} color="orange" trend="-3%" />
        <StatCard title="Threat Level" value={threatLevel} icon={AlertTriangle} color="purple" trend="STABLE" />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard title="Attack Timeline (24h)" subtitle="Real-time monitoring of security events">
            <Line data={chartData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { 
                legend: { display: false },
                tooltip: {
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  titleColor: 'white',
                  bodyColor: 'white',
                  borderColor: 'rgba(59, 130, 246, 0.5)',
                  borderWidth: 1
                }
              },
              scales: {
                y: { 
                  beginAtZero: true, 
                  grid: { color: 'rgba(75, 85, 99, 0.3)' },
                  ticks: { color: 'rgba(156, 163, 175, 0.8)' }
                },
                x: { 
                  grid: { color: 'rgba(75, 85, 99, 0.3)' },
                  ticks: { color: 'rgba(156, 163, 175, 0.8)' }
                }
              }
            }} />
          </ChartCard>
        </div>

        <ChartCard title="Attack Types" subtitle="Distribution of attack vectors">
          <Doughnut data={attackTypeData} options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { 
              legend: { 
                position: 'bottom',
                labels: { color: 'white', font: { size: 12 } }
              },
              tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                titleColor: 'white',
                bodyColor: 'white'
              }
            }
          }} />
        </ChartCard>
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Severity Distribution" subtitle="Risk assessment breakdown">
          <Bar data={severityData} options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: { 
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                titleColor: 'white',
                bodyColor: 'white'
              }
            },
            scales: {
              y: { 
                beginAtZero: true,
                grid: { color: 'rgba(75, 85, 99, 0.3)' },
                ticks: { color: 'rgba(156, 163, 175, 0.8)' }
              },
              x: { 
                grid: { display: false },
                ticks: { color: 'rgba(156, 163, 175, 0.8)' }
              }
            }
          }} />
        </ChartCard>

        {/* Real-time Activity Feed */}
        <div className="glassmorphism-card rounded-2xl p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Radar className="mr-2 w-5 h-5" />
            Live Activity Feed
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {analytics?.logs?.slice(0, 8).map((log, index) => (
              <div key={index} className="flex items-center justify-between p-3 glassmorphism-alert-item rounded-lg border-l-4 border-blue-500 hover:bg-white/10 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <div>
                    <span className="text-sm text-white">{log[1]} attempted login</span>
                    <div className="text-xs text-gray-400">Username: "{log[5] || 'unknown'}"</div>
                  </div>
                </div>
                <span className="text-xs text-gray-400">{new Date(log[0]).toLocaleTimeString()}</span>
              </div>
            )) || (
              <div className="text-center text-gray-400 py-8">
                <Search className="text-4xl mb-2 w-10 h-10 mx-auto" />
                <p>No recent activity detected</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable Components
function StatCard({ title, value, icon, color, trend }) {
  const colorClasses = {
    blue: 'glassmorphism-stat-blue',
    green: 'glassmorphism-stat-green',
    red: 'glassmorphism-stat-red',
    orange: 'glassmorphism-stat-yellow',
    purple: 'glassmorphism-stat-blue',
  };

  const trendColor = trend?.startsWith('+') ? 'text-green-400' : trend?.startsWith('-') ? 'text-red-400' : 'text-gray-400';

  return (
    <div className={`glassmorphism-card rounded-2xl p-6 transition-all hover:scale-105 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <p className={`text-xs mt-1 ${trendColor}`}>{trend}</p>
        </div>
        <div className="text-3xl opacity-80">
          {React.createElement(icon, { className: "w-8 h-8" })}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="glassmorphism-card rounded-2xl p-6 hover:border-gray-600 transition-colors">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className="h-64">
        {children}
      </div>
    </div>
  );
}

// Placeholder components for other tabs
function AttacksTab({ analytics, mockData }) {
  // Chart data for attack analysis
  const attackTypeData = {
    labels: ['Phishing', 'Malware', 'DDoS', 'SQL Injection', 'Brute Force', 'XSS'],
    datasets: [{
      data: [30, 25, 20, 12, 8, 5],
      backgroundColor: [
        '#0e38b1', '#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'
      ],
      borderWidth: 0
    }]
  };

  const trendData = {
    labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
    datasets: [{
      label: 'Attack Volume',
      data: [12, 19, 25, 42, 28, 35],
      borderColor: '#0e38b1',
      backgroundColor: 'rgba(14, 56, 177, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const countryData = {
    labels: ['Russia', 'China', 'North Korea', 'Iran', 'Brazil'],
    datasets: [{
      label: 'Attacks',
      data: [145, 123, 89, 67, 45],
      backgroundColor: '#0e38b1',
      borderRadius: 4
    }]
  };

  return (
    <div className="space-y-6">
      {/* Attack Analysis Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attack Types Distribution */}
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center space-x-3 mb-6">
            <BarChart3 className="h-6 w-6 text-[#0e38b1]" />
            <h2 className="text-xl font-bold">Attack Types Distribution</h2>
          </div>
          <div className="h-64">
            <Pie 
              data={attackTypeData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: 'white', padding: 20 }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Attack Trends */}
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center space-x-3 mb-6">
            <TrendingUp className="h-6 w-6 text-[#0e38b1]" />
            <h2 className="text-xl font-bold">Attack Trends (24h)</h2>
          </div>
          <div className="h-64">
            <Line 
              data={trendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: { color: 'rgba(255,255,255,0.1)' },
                    ticks: { color: 'white' }
                  },
                  x: {
                    grid: { color: 'rgba(255,255,255,0.1)' },
                    ticks: { color: 'white' }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Top Attacking Countries */}
      <div className="glassmorphism-card p-6 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <Activity className="h-6 w-6 text-[#0e38b1]" />
          <h2 className="text-xl font-bold">Top Attacking Countries</h2>
        </div>
        <div className="h-64">
          <Bar 
            data={countryData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(255,255,255,0.1)' },
                  ticks: { color: 'white' }
                },
                x: {
                  grid: { display: false },
                  ticks: { color: 'white' }
                }
              }
            }}
          />
        </div>
      </div>

      {/* Recent Security Events */}
      <div className="glassmorphism-card p-6 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <Eye className="h-6 w-6 text-[#0e38b1]" />
          <h2 className="text-xl font-bold">Recent Security Events</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-3">Time</th>
                <th className="text-left p-3">Event Type</th>
                <th className="text-left p-3">Source IP</th>
                <th className="text-left p-3">Threat Level</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(mockData || []).slice(0, 10).map((event, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">{new Date(event.timestamp).toLocaleTimeString()}</td>
                  <td className="p-3">{event.eventType}</td>
                  <td className="p-3">{event.sourceIP}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      event.threatLevel === 'Critical' ? 'bg-red-500/20 text-red-300' :
                      event.threatLevel === 'High' ? 'bg-orange-500/20 text-orange-300' :
                      event.threatLevel === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-green-500/20 text-green-300'
                    }`}>
                      {event.threatLevel}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      event.blocked ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                    }`}>
                      {event.blocked ? 'Blocked' : 'Allowed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GeographyTab({ analytics, geoData, mapRef }) {
  return (
    <div className="space-y-6">
      {/* Geographic Intelligence Section */}
      <div className="glassmorphism-card p-8 rounded-xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-[#0e38b1] to-[#0042ff] rounded-xl shadow-lg">
              <Globe className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Geographic Intelligence</h2>
              <p className="text-gray-400 text-sm mt-1">Real-time global threat monitoring</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-white/5 rounded-full px-4 py-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="relative">
              <div 
                ref={mapRef} 
                className="h-96 md:h-[500px] lg:h-[600px] w-full rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl border border-white/10"
                style={{ 
                  minHeight: '600px',
                  background: 'linear-gradient(135deg, rgba(14, 56, 177, 0.1) 0%, rgba(0, 66, 255, 0.05) 50%, rgba(30, 41, 59, 0.8) 100%)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              />
              <div className="absolute top-4 left-4 bg-black/20 backdrop-blur-sm rounded-lg px-3 py-2 text-sm text-white/80 border border-white/10">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Live Threat Map</span>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/20 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-white/60 border border-white/10">
                Real-time geographic threat intelligence
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg">Top Attack Origins</h3>
              <div className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded-full">
                Last 24h
              </div>
            </div>
            <div className="max-h-[520px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20">
              {(geoData || []).slice(0, 10).map((country, index) => (
                <div key={index} className="group flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-300 mb-3 border border-white/5 hover:border-white/20">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <MapPin className="h-5 w-5 text-[#0e38b1] group-hover:text-[#0042ff] transition-colors duration-200" />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-[8px] flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">{country.country}</span>
                      <div className="text-xs text-gray-400 mt-1">
                        {Math.floor(Math.random() * 50 + 10)} active threats
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{country.attacks}</div>
                    <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                      country.severity === 'critical' ? 'text-red-300 bg-red-500/20' :
                      country.severity === 'high' ? 'text-orange-300 bg-orange-500/20' :
                      country.severity === 'medium' ? 'text-yellow-300 bg-yellow-500/20' : 'text-green-300 bg-green-500/20'
                    }`}>
                      {country.severity}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Geographic Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Countries Detected</p>
              <p className="text-3xl font-bold text-[#0e38b1]">{geoData?.length || 0}</p>
            </div>
            <Globe className="h-12 w-12 text-[#0e38b1] opacity-80" />
          </div>
        </div>
        
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Attacks</p>
              <p className="text-3xl font-bold text-orange-400">
                {geoData?.reduce((sum, country) => sum + country.attacks, 0) || 0}
              </p>
            </div>
            <Target className="h-12 w-12 text-orange-400 opacity-80" />
          </div>
        </div>
        
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Critical Origins</p>
              <p className="text-3xl font-bold text-red-400">
                {geoData?.filter(country => country.severity === 'critical').length || 0}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-red-400 opacity-80" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ThreatsTab({ analytics, blacklistedIPs, addToBlacklist }) {
  return (
    <div className="space-y-6">
      <div className="glassmorphism-card rounded-2xl p-8 text-center">
        <AlertTriangle className="text-6xl mb-4 w-16 h-16 mx-auto" style={{ color: '#0e38b1' }} />
        <h2 className="text-2xl font-bold mb-2">Threat Intelligence</h2>
        <p className="text-gray-400 mb-6">AI-powered threat detection and response</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-red-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-400">{blacklistedIPs.size}</div>
            <div className="text-sm text-gray-400">Blocked IPs</div>
          </div>
          <div className="bg-yellow-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-400">3</div>
            <div className="text-sm text-gray-400">Active Threats</div>
          </div>
          <div className="bg-green-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-400">97%</div>
            <div className="text-sm text-gray-400">Protection Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportsTab({ analytics, exportData, exportCSV, generatePDF }) {
  return (
    <div className="space-y-6">
      {/* Export Options */}
      <div className="glassmorphism-card p-6 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <FileText className="h-6 w-6 text-[#0e38b1]" />
          <h2 className="text-xl font-bold">Data Export & Reporting</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/5 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">CSV Export</h3>
            <p className="text-gray-400 text-sm mb-4">Download raw security log data for external analysis</p>
            <button
              onClick={exportCSV}
              className="w-full px-4 py-2 rounded-lg transition-all hover:scale-105 flex items-center justify-center space-x-2"
              style={{
                background: 'linear-gradient(135deg, #059669, #10b981)',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
              }}
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
          
          <div className="bg-white/5 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-3">PDF Report</h3>
            <p className="text-gray-400 text-sm mb-4">Generate comprehensive security analysis report</p>
            <button
              onClick={generatePDF}
              data-pdf-button
              className="w-full px-4 py-2 rounded-lg transition-all hover:scale-105 flex items-center justify-center space-x-2"
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
              }}
            >
              <FileText className="w-4 h-4" />
              <span>Generate PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Export Statistics */}
      <div className="glassmorphism-card p-6 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <BarChart3 className="h-6 w-6 text-[#0e38b1]" />
          <h2 className="text-xl font-bold">Export Statistics</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-500/10 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-400">{analytics?.total || 50}</div>
            <div className="text-sm text-gray-400">Total Records Available</div>
          </div>
          <div className="bg-green-500/10 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-400">1000</div>
            <div className="text-sm text-gray-400">Max CSV Export Limit</div>
          </div>
          <div className="bg-purple-500/10 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-400">15</div>
            <div className="text-sm text-gray-400">PDF Report Events</div>
          </div>
        </div>
      </div>

      {/* Security Notices */}
      <div className="glassmorphism-card p-6 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <AlertTriangle className="h-6 w-6 text-orange-400" />
          <h2 className="text-xl font-bold">Security & Compliance Notices</h2>
        </div>
        
        <div className="space-y-4">
          <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <ShieldAlert className="h-5 w-5 text-red-300" />
              <h3 className="text-red-300 font-semibold">Intrusion Detection Active</h3>
            </div>
            <p className="text-sm text-gray-300">
              Unauthorized access attempts are automatically logged and reported to:
            </p>
            <ul className="text-sm text-gray-400 mt-2 space-y-1">
              <li>• rloganjr1025@gmail.com</li>
              <li>• absons.ramon@gmail.com</li>
            </ul>
            <div className="flex items-center space-x-2 mt-3">
              <AlertTriangle className="h-4 w-4 text-yellow-400" />
              <p className="text-xs text-gray-500">
                Note: Email notifications require backend infrastructure in production
              </p>
            </div>
          </div>
          
          <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Database className="h-5 w-5 text-blue-300" />
              <h3 className="text-blue-300 font-semibold">Data Retention Policy</h3>
            </div>
            <p className="text-sm text-gray-300">
              Security logs are retained for compliance and analysis purposes. All data is encrypted and access-controlled.
            </p>
          </div>
          
          <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <h3 className="text-green-300 font-semibold">System Status</h3>
            </div>
            <p className="text-sm text-gray-300">
              All security monitoring systems operational. Real-time threat detection active.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
